import { Component, OnInit, ViewChild, ViewEncapsulation, signal } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ConfirmationService } from "primeng/api";
import { IBrand } from '../../shared/interfaces/brand';
import { BrandProfileService } from '../../services/construct/brand-profile.service';
import { UserContextService } from '../../services/safeguard/user-context.service';
import { SharedService } from "../../shared/services/shared.service";
import { BrandAdminHeaderComponent } from '../../shared/components/brand-admin-header/brand-admin-header.component';
import { EnrollVerificationConfig } from './EnrollVerificationConfig';
import { EvConfigChangeRequest } from './model/EvConfigChangeRequest';
import { EVChannelFieldGroups } from './model/EVChannelFieldGroups';
import { EvField } from "./model/EvField";


@Component({
  selector: 'app-ev-editor',
  standalone: false,
  templateUrl: './ev-editor.component.html',
  styleUrl: './ev-editor.component.scss',
  encapsulation: ViewEncapsulation.None,
  })

export class EvEditorComponent implements OnInit {

  @ViewChild(BrandAdminHeaderComponent, { static: true }) child!: BrandAdminHeaderComponent;

  showItem = signal(true);
  toggle() { 
    this.showItem.update(v => !v); 
  }  

  private initialConfigState: string = '';//To check if there are unsaved changes

  configData: EnrollVerificationConfig = {
    "brandCode": "",
    "evFields": [],
    "evChannels": [],
    "evChannelFieldGroups": [],
    "changeDescription": "change description"
  };

  request!: EvConfigChangeRequest;
  // Options for dropdowns
  inputTypes = ["button", "checkbox", "color", "date", "email", "file", "hidden", "image", "month", "number", "password", "radio", "range", "reset", "search", "submit", "tel", "text", "time", "url", "week"];
  comparisonTypes = ["EQUALS", "EQUALS_IGNORE_CASE", "CONTAINS", "CONTAINED"];
  channelComparisonTypes = ["EQUALS", "CONTAINS", "STARTS_WITH", "ENDS_WITH"];
  fieldUsageTypes = ["NONE", "ID", "VERIFICATION", "LOOKUP", "EXTRA_DATA"];
  accountFetchMethods = ["NONE", "FETCH_BY_ID", "LOCAL_LOOKUP", "LOCAL_LOOKUP_AND_FETCH", "REMOTE_LOOKUP", "REMOTE_LOOKUP_AND_FETCH"];
  accountFetchMethodHelp =
    "<b>FETCH_BY_ID</b>: Use the collected account# to fetch.<br>" +
    "<b>LOCAL_LOOKUP</b>: Use the collected search criteria values to make a special lookup call to obtain the account ID. Does not fetch the account data.<br>" +
    "<b>REMOTE_LOOKUP</b>: Use the collected search criteria values to make a special lookup call to obtain the account ID. Does not fetch the account data.<br>" +
    "<b>LOCAL_LOOKUP_AND_FETCH</b>: Use the collected search criteria values to make a 'special' lookup call to determine the account number, then using it to obtain the account data as DTO.<br>" +
    "<b>REMOTE_LOOKUP_AND_FETCH</b>: Use the collected search criteria values to make a 'special' lookup call to determine the account number, then using it to obtain the account data as DTO.";
  channelTypes = ["SYSTEM_BUILT_IN", "DEFAULT", "WILDCARD", "CUSTOM"];
  dataTypes = ["string", "number", "date", "phone", "zip5"];


  updatedJson: string = '';


  currentForm: string = "EvEditor";
  titleMessage = "Enrollment Verification Editor";
  userform!: FormGroup;

  isDetailsShown = false;
  isCurrentBrandShown = true;

  currentBrand: IBrand;

  openBrandSelectionDialog!: boolean;
  changeDescription: string = '';

  evFieldsCollapsed = false;
  evChannelsCollapsed = false;
  mappingsCollapsed = false;
  configPresent: string = 'TBD';
  leftChannels: any[] = [];
  rightChannels: any[] = [];


  constructor(
    private fb: FormBuilder,
    private brandProfileService: BrandProfileService,
    private sharedService: SharedService,
    private userContextService: UserContextService,
    private confirmationService: ConfirmationService) {
    this.currentBrand = this.sharedService.getDummyBrand();
  }

  ngOnInit(): void {
    if (this.sharedService.isCurrentBrandKnown()) {
      this.currentBrand = this.sharedService.getCurrentBrand();
      this.child.makeTitleMessage(this.currentBrand, this.titleMessage);
      this.openBrandSelectionDialog = false;
      this.getBrandEvConfig(this.currentBrand.brandCode, this.currentBrand.baseUrl)
    } else {
      this.isCurrentBrandShown = false;
      this.openBrandSelectionDialog = true;
    }


  }

  // When the user opens up the dialog to select a brand
  onChooseBrandClick(event: any) {
    this.openBrandSelectionDialog = true;
  }

  // When a brand is chosen
  chooseBrand(item: IBrand) {
    this.openBrandSelectionDialog = false;
    if (item != null) {
      this.isCurrentBrandShown = false;
      this.currentBrand = item;
      this.child.makeTitleMessage(this.currentBrand, this.titleMessage);
      this.sharedService.setCurrentBrand(item);
      this.getBrandEvConfig(this.currentBrand.brandCode, this.currentBrand.baseUrl);
    }
  }

  getBrandEvConfig(brandCode: string, brandUrl: string) {
    // Call the service to invoke a Web API call
    this.brandProfileService.getEvConfig(brandCode, brandUrl)
      .subscribe({
        next: (response) => {
          this.configData = response;
          // Initialize UI-specific properties
          if (this.configData.evFields) {
            this.configData.evFields.forEach(field => {
              field.collapsed = false;
              field.showMore = false;
              field.showStyle = false;
              if (!field.regexReplacement) {
                field.regexReplacement = { matchPattern: '', replacement: '' };
              }
            });
          }
          if (this.configData.evChannelFieldGroups) {
            this.configData.evChannelFieldGroups.forEach(mapping => {
              mapping.collapsed = false;
            });
          }
          this.changeDescription = ''; // Clear the change description
          this.initialConfigState = JSON.stringify(this.configData); // Capture initial state
          if (this.configData && this.configData.brandCode === brandCode) {
            this.configPresent = 'present';
          } else {
            this.configPresent = 'absent';
          }
          this.splitChannels();
          this.loadCollapseState();

        },
        error: (error) => {
          this.configPresent = 'absent';
          this.alertTheUser("Error", error.message);
        },
        complete: () => {
        }
      });
  }

  private checkDuplicateChannelOrFieldRefs(): boolean {

    for (let i = 0; i < this.configData.evChannelFieldGroups.length; i++) {
      const mapping = this.configData.evChannelFieldGroups[i];

      // Defensive: mapping itself might be undefined/null
      if (!mapping) { continue; }

      // Check for duplicate channel references
      const channelRefs = mapping.evChannelRefs.map(ref => ref.channelName);
      const uniqueChannelRefs = new Set(channelRefs);
      if (uniqueChannelRefs.size !== channelRefs.length) {
        this.alertTheUser("Warning",`Duplicate Channel Reference found in "Ev Channel Field Group: ${i + 1}". Please ensure all channel references are unique.`);
        return true;
      }

      // Check for duplicate field references
      const fieldRefs = mapping.evFieldRefs.map(ref => ref.fieldName);
      const uniqueFieldRefs = new Set(fieldRefs);
      if (uniqueFieldRefs.size !== fieldRefs.length) {
        this.alertTheUser("Warning",`Duplicate Field Reference found in "Ev Channel Field Group: ${i + 1}". Please ensure all field references are unique.`);
        return true;
      }
    }
    return false;
  }

  private checkDuplicateFieldDefinition(): boolean {
    const fieldNames = this.configData.evFields.map(f => f.fieldName);
    const uniqueFieldNames = new Set(fieldNames);
    if (uniqueFieldNames.size !== fieldNames.length) {
      this.alertTheUser("Warning", 'Duplicate field name found. Please ensure all field names are unique.');
      return true;
    }
    return false;
  }

  private checkDuplicateChannelDefinition(): boolean {
    const channelNames = this.configData.evChannels.map(c => c.channelName);
    const uniqueChannelNames = new Set(channelNames);
    if (uniqueChannelNames.size !== channelNames.length) {
      this.alertTheUser("Warning", 'Duplicate channel name found. Please ensure all channel names are unique.');
      return true;
    }
    return false;
  }

  saveConfig(): void {
    // 1. Validate for duplicate channel names
    if (this.checkDuplicateFieldDefinition()) {
      return;// Exit if duplicate field definition found
    }
    if (this.checkDuplicateChannelDefinition()) {
      return;// Exit if duplicate channel definition found
    }

    // 2. Validate for duplicate references
    if (this.checkDuplicateChannelOrFieldRefs()) {
      return; // Exit if duplicates are found
    }


    // Create a snapshot of the current data
    const currentConfigState = JSON.stringify(this.configData);

    // 3. Check if the data has actually changed
    if (currentConfigState === this.initialConfigState) {
      this.alertTheUser("Warning", 'There are no changes to save.');
      return; // Exit the function
    }

    // 4.  Ensure a change description is provided
    if (!this.changeDescription || this.changeDescription.trim() === '') {
      this.alertTheUser("Warning", 'Please provide a description for your changes before saving.');
      return;
    }

    // 5. If checks pass, proceed with saving
    this.request = { changeDescription: this.changeDescription, evConfig: this.configData };
    this.brandProfileService.saveEvConfig(this.currentBrand.brandCode, this.currentBrand.baseUrl, this.request)
      .subscribe({
        next: () => {
          this.alertTheUser('Processed', 'Configuration saved successfully!');

          // Update the description in the data object to be displayed in the header
          this.configData.changeDescription = this.changeDescription;

          // Clear the input field
          this.changeDescription = '';

          // Update the initial state to the new, saved state, including the new description
          this.initialConfigState = JSON.stringify(this.configData);
        },
        error: (error) => {
          this.alertTheUser('Error', 'Failed to save configuration: ' + error.message);
        }
      });
  }

  bootstrapDefaultEvConfigIfAbsent(): void {
    this.brandProfileService.bootstrapDefaultEvConfigIfAbsent(this.currentBrand.brandCode, this.currentBrand.baseUrl)
      .subscribe({
        next: (response) => {
          this.configData = response;
          // Initialize UI-specific properties
          if (this.configData.evFields) {
            this.configData.evFields.forEach(field => {
              field.collapsed = false;
              field.showMore = false;
              field.showStyle = false;
              if (!field.regexReplacement) {
                field.regexReplacement = { matchPattern: '', replacement: '' };
              }
              if (field.fieldName === 'account') {
                field.accountIdentity = true;
              }
            });
          }
          if (this.configData.evChannelFieldGroups) {
            this.configData.evChannelFieldGroups.forEach(mapping => {
              mapping.collapsed = false;
            });
          }
          this.initialConfigState = JSON.stringify(this.configData); // Capture initial state
          if (this.configData && this.configData.brandCode === this.currentBrand.brandCode) {
            this.configPresent = 'present';
          } else {
            this.configPresent = 'absent';
          }
          this.splitChannels();
          this.loadCollapseState();
        },
        error: (error) => {
          this.alertTheUser("Error", error.message);
        },
        complete: () => {
        }
      });
  }

  alertTheUser(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      key: 'email-sms-template',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info p-button-rounded",
      accept: () => {
        return;
      }
    });
  }




  // --- ADD NEW ITEMS ---

  addNewField(): void {
    this.configData.evFields.push({
      collapsed: false,
      showMore: false, // Initialize showMore to false
      showStyle: false, // Initialize showStyle to false
      fieldName: `newField${this.configData.evFields.length + 1}`,
      label: 'New Field',
      placeholder: 'New Field',
      inputType: 'text',
      dataType: 'string',
      pattern: '',
      maxLength: 100,
      fieldValueComparisonType: 'EQUALS_IGNORE_CASE',
      invalidFormatMsg: '',
      mask: null,
      accountIdentity: false,
      aliased: false,
      ignoredIfProvided: false,
      displayed: false,
      leftPadded: false,
      rightPadded: false,
      secondaryAccountIdentity: false,
      persisted: false,
      uppercase: false,
      useTextBox: false,
      defaultValue: null,
      regexReplacement: { matchPattern: '', replacement: '' },
      originVariableName: null,
      padChar: null,
      paddedSize: 0,
      transformFormatPattern: null,
      styles: {},
    });
  }

  addNewChannel(): void {
    this.configData.evChannels.push({
      channelName: `NewChannel${this.configData.evChannels.length + 1}`,
      channelType: 'CUSTOM',
      channelDescription: 'A new custom channel',
      enabled: true
    });
    this.splitChannels();
  }

  addNewMapping(): void {
    this.configData.evChannelFieldGroups.push({
      evChannelRefs: [{
        channelName: this.configData.evChannels[0]?.channelName || '',
        channelComparisonType: 'EQUALS', method: 'FETCH_BY_ID',
        enabled: true
      }],
      evFieldRefs: [],
      collapsed: false,
      enabled: true
    });
  }

  addNewChannelRef(mapping: EVChannelFieldGroups): void {
    mapping.evChannelRefs.push({
      channelName: this.configData.evChannels[0]?.channelName || '',
      channelComparisonType: 'EQUALS', method: 'FETCH_BY_ID',
      enabled: true
    });
  }

  addNewFieldRef(mapping: EVChannelFieldGroups): void {
    mapping.evFieldRefs.push({
      fieldName: this.configData.evFields[0]?.fieldName || '', // Default to first available field
      fieldUsage: 'NONE',
      order: mapping.evFieldRefs.length + 1
    });
  }

  removeFieldRef(mapping: EVChannelFieldGroups, index: number): void {
    mapping.evFieldRefs.splice(index, 1);
    this.updateFieldRefOrder(mapping); // Re-sequence the order after removal
  }

  moveFieldRefUp(mapping: EVChannelFieldGroups, index: number): void {
    if (index === 0) {
      return; // Already at the top
    }
    // Move item in the array
    const fieldRefToMove = mapping.evFieldRefs.splice(index, 1)[0]!;
    mapping.evFieldRefs.splice(index - 1, 0, fieldRefToMove);

    // Update the order property for all items
    this.updateFieldRefOrder(mapping);
  }

  moveFieldRefDown(mapping: EVChannelFieldGroups, index: number): void {
    if (index >= mapping.evFieldRefs.length - 1) {
      return; // Already at the bottom
    }
    // Move item in the array
    const fieldRefToMove = mapping.evFieldRefs.splice(index, 1)[0]!;
    mapping.evFieldRefs.splice(index + 1, 0, fieldRefToMove);

    // Update the order property for all items
    this.updateFieldRefOrder(mapping);
  }

  /**
   * Iterates over the field references in a mapping and updates their 'order'
   * property to match their current index in the array.
   */
  updateFieldRefOrder(mapping: EVChannelFieldGroups): void {
    mapping.evFieldRefs.forEach((ref, idx) => {
      ref.order = idx + 1;
    });
  }


  // --- REMOVE ITEMS ---

  removeField(index: number): void {
    this.configData.evFields.splice(index, 1);
  }

  removeChannel(index: number): void {
    this.configData.evChannels.splice(index, 1);
    this.splitChannels();
  }

  removeMapping(index: number): void {
    this.configData.evChannelFieldGroups.splice(index, 1);
  }

  removeChannelRef(mapping: EVChannelFieldGroups, refIndex: number): void {
    mapping.evChannelRefs.splice(refIndex, 1);
  }

  removeStyle(field: any, key: string): void {
    // Ensure the styles object and the key exist before proceeding.
    if (field.styles && field.styles.hasOwnProperty(key)) {
      // Create a new object, excluding the key to be removed
      const { [key]: removed, ...newStyles } = field.styles;
      field.styles = newStyles;
    }
  }

  addPendingStyle(field: any): void {
    const newKey = field['_newStyleKey'];
    const newValue = field['_newStyleValue'];

    // Proceed only if both a key and a value have been provided.
    if (newKey && newKey.trim() && newValue) {
      // Create a new object with the existing styles and add the new one.
      field.styles = {
        ...field.styles,
        [newKey.trim()]: newValue
      };

      // Clear the temporary properties to reset the input fields.
      field['_newStyleKey'] = '';
      field['_newStyleValue'] = '';
    }
  }

  trackByStyleKey(index: number, item: any): string {
    return item.key;
  }


  moveFieldUp(index: number): void {
    if (index === 0) {
      return; // Already at the top
    }
    // Remove the field from its current position and insert it one position higher
    const fieldToMove = this.configData.evFields.splice(index, 1)[0]!;
    this.configData.evFields.splice(index - 1, 0, fieldToMove);
  }

  moveFieldDown(index: number): void {
    if (index >= this.configData.evFields.length - 1) {
      return; // Already at the bottom
    }
    // Remove the field from its current position and insert it one position lower
    const fieldToMove = this.configData.evFields.splice(index, 1)[0]!;
    this.configData.evFields.splice(index + 1, 0, fieldToMove);
  }

  moveMappingUp(index: number): void {
    if (index === 0) {
      return; // Already at the top
    }
    const mappingToMove = this.configData.evChannelFieldGroups.splice(index, 1)[0]!;
    this.configData.evChannelFieldGroups.splice(index - 1, 0, mappingToMove);
  }

  moveMappingDown(index: number): void {
    if (index >= this.configData.evChannelFieldGroups.length - 1) {
      return; // Already at the bottom
    }
    const mappingToMove = this.configData.evChannelFieldGroups.splice(index, 1)[0]!;
    this.configData.evChannelFieldGroups.splice(index + 1, 0, mappingToMove);
  }

  // --- UTILITY ---

  splitChannels(): void {
    if (!this.configData.evChannels) {
      this.leftChannels = [];
      this.rightChannels = [];
      return;
    }
    const totalChannels = this.configData.evChannels.length;
    const midPoint = Math.ceil(totalChannels / 2);

    this.leftChannels = this.configData.evChannels.slice(0, midPoint);
    this.rightChannels = this.configData.evChannels.slice(midPoint);
  }

  private getStorageKey(): string {
    if (!this.currentBrand?.brandCode) {
      return ''; // Cannot save state without a brand context
    }
    return `evEditorCollapseState_${this.currentBrand.brandCode}`;
  }

  /**
   * Saves the current collapse/expand state of all sections to localStorage.
   */
  saveCollapseState(): void {
    const storageKey = this.getStorageKey();
    if (!storageKey) return;

    const state = {
      evFields: this.evFieldsCollapsed,
      evChannels: this.evChannelsCollapsed,
      mappings: this.mappingsCollapsed,
      // Store the state of individual fields by their unique fieldName for robustness
      fieldStates: this.configData.evFields.reduce((acc, field) => {
        if (field.fieldName) {
          acc[field.fieldName] = {
            collapsed: !!field.collapsed,
            showMore: !!field.showMore,
            showStyle: !!field.showStyle
          };
        }
        return acc;
      }, {} as { [key: string]: { collapsed: boolean, showMore: boolean, showStyle: boolean } }),
      // Store the state of individual mappings by their index
      mappingStates: this.configData.evChannelFieldGroups.map(mapping => !!mapping.collapsed)
    };

    localStorage.setItem(storageKey, JSON.stringify(state));
  }

  /**
   * Loads and applies the collapse/expand state from localStorage.
   */
  loadCollapseState(): void {
    const storageKey = this.getStorageKey();
    if (!storageKey) return;

    const savedStateJSON = localStorage.getItem(storageKey);
    const savedState = savedStateJSON ? JSON.parse(savedStateJSON) : {};

    this.evFieldsCollapsed = savedState.evFields ?? false;
    this.evChannelsCollapsed = savedState.evChannels ?? false;
    this.mappingsCollapsed = savedState.mappings ?? false;

    this.configData.evFields.forEach(field => {
      const fieldState = savedState.fieldStates?.[field.fieldName];
      field.collapsed = fieldState?.collapsed ?? field.collapsed; // Keep default if no saved state
      field.showMore = fieldState?.showMore ?? field.showMore;
      field.showStyle = fieldState?.showStyle ?? field.showStyle;
    });

    if (this.configData.evChannelFieldGroups) {
      this.configData.evChannelFieldGroups.forEach((mapping, index) => {
      mapping.collapsed = savedState.mappingStates?.[index] ?? mapping.collapsed;
    });
    }
    
  }

  /**
   * Toggles the collapsed state of a main section and saves the change.
   */
  toggleSection(section: 'evFields' | 'evChannels' | 'mappings'): void {
    if (section === 'evFields') this.evFieldsCollapsed = !this.evFieldsCollapsed;
    if (section === 'evChannels') this.evChannelsCollapsed = !this.evChannelsCollapsed;
    if (section === 'mappings') this.mappingsCollapsed = !this.mappingsCollapsed;
    this.saveCollapseState();
  }

  /**
   * Toggles the collapsed state of an individual field and saves the change.
   */
  toggleFieldCollapse(field: any): void {
    field.collapsed = !field.collapsed;
    this.saveCollapseState();
  }

  /**
   * Toggles the showMore state of an individual field and saves the change.
   */
  toggleShowMore(field: EvField): void {
    field.showMore = !field.showMore;
    this.saveCollapseState();
  }

  /**
   * Toggles the showStyle state of an individual field and saves the change.
   */
  toggleShowStyle(field: EvField): void {
    field.showStyle = !field.showStyle;
    this.saveCollapseState();
  }

  /**
   * Toggles the collapsed state of an individual mapping and saves the change.
   */
  toggleMappingCollapse(mapping: any): void {
    mapping.collapsed = !mapping.collapsed;
    this.saveCollapseState();
  }


}
